import { useEffect, useRef, useState } from "react";
import type { CartV2Item } from "./cart-v2.js";
import {
  CART_FINDER_DEBOUNCE_MS,
  CART_FINDER_FETCH_LIMIT,
  CART_FINDER_PAGE_SIZE,
  formatSearchHitCard,
  nextFinderPageOffset,
  toCartAddHit,
  type CartFinderCard,
} from "./cart-product-finder-v2.js";
import { formatUah } from "./money-v2.js";
import { serverSearch } from "./search-server-client-v2.js";
import type { StoreV2Lang } from "./store-v2-locale.js";
import { t } from "./store-v2-ui-copy.js";

export type CartAddSearchHit = {
  id: string;
  title: string;
  imageUrl: string | null;
  priceUah: number | null;
  availability?: string | null;
};

export function CartProductFinderV2({
  lang,
  items,
  onAdd,
  compact = false,
  openLabel,
}: {
  lang: StoreV2Lang;
  items: CartV2Item[];
  onAdd: (hit: CartAddSearchHit) => void;
  /** @deprecated R4.4 — cart inline flow never navigates to full search. */
  onShowAllResults?: ((query: string) => void) | undefined;
  compact?: boolean;
  openLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [cards, setCards] = useState<CartFinderCard[]>([]);
  const [offset, setOffset] = useState(0);
  const [addedId, setAddedId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const q = query.trim();
    setOffset(0);
    if (q.length < 2) {
      setCards([]);
      return;
    }
    const ac = new AbortController();
    const timer = window.setTimeout(() => {
      void serverSearch(q, { limit: CART_FINDER_FETCH_LIMIT, signal: ac.signal }).then((res) => {
        if (ac.signal.aborted) return;
        const sellable = (res.hits ?? []).filter((h) => h.availability !== "out_of_stock");
        setCards(sellable.map((h) => formatSearchHitCard(h, lang)));
      });
    }, CART_FINDER_DEBOUNCE_MS);
    return () => {
      ac.abort();
      window.clearTimeout(timer);
    };
  }, [query, lang]);

  useEffect(() => {
    if (!open) return;
    const node = inputRef.current;
    if (!node) return;
    node.focus();
    node.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [open]);

  const qtyById = new Map(items.map((i) => [i.id, i.qty]));
  const page = cards.slice(offset, offset + CART_FINDER_PAGE_SIZE);
  const canCycle = cards.length > CART_FINDER_PAGE_SIZE;

  const addHit = (card: CartFinderCard) => {
    onAdd(toCartAddHit(card));
    setAddedId(card.id);
    window.setTimeout(() => setAddedId((cur) => (cur === card.id ? null : cur)), 1400);
  };

  return (
    <section
      ref={rootRef}
      className={`sv2-cart-finder${compact ? " is-compact" : ""}`}
      data-testid="sv2-cart-inline-add"
      data-expanded={open ? "1" : "0"}
    >
      {!open ? (
        <button
          type="button"
          className="sv2-cta sv2-secondary sv2-cart-finder-open"
          data-testid="sv2-cart-finder-open"
          data-sv2-btn-variant="secondary"
          onClick={() => setOpen(true)}
        >
          {openLabel ?? t(lang, "checkout_add_more")}
        </button>
      ) : (
        <div className="sv2-cart-finder-search-row">
          <span className="sv2-cart-finder-search-icon" aria-hidden="true">
            🔍
          </span>
          <input
            ref={inputRef}
            className="sv2-promo-input sv2-cart-finder-input"
            data-testid="sv2-cart-inline-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t(lang, "cart_search_placeholder")}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="search"
          />
          {canCycle ? (
            <button
              type="button"
              className="sv2-cart-finder-next"
              data-testid="sv2-cart-finder-next"
              aria-label={lang === "en" ? "Next 3 results" : "Наступні 3 результати"}
              onClick={() => setOffset((cur) => nextFinderPageOffset(cur, cards.length))}
            >
              {lang === "en" ? "Next" : "Ще"}
            </button>
          ) : null}
        </div>
      )}
      {open && page.length > 0 ? (
        <div className="sv2-cart-finder-hits" data-testid="sv2-cart-finder-hits">
          {page.map((card) => {
            const qty = qtyById.get(card.id) ?? 0;
            const inCart = qty > 0;
            const canAdd = card.priceUah != null && card.priceUah > 0;
            const justAdded = addedId === card.id;
            return (
              <article
                key={`${card.id}:${offset}`}
                className="sv2-cart-finder-card"
                data-testid={`sv2-cart-inline-hit-${card.id}`}
              >
                {card.imageUrl ? (
                  <img className="sv2-cart-finder-img" src={card.imageUrl} alt="" loading="lazy" />
                ) : (
                  <div className="sv2-cart-finder-img" aria-hidden="true">
                    🍜
                  </div>
                )}
                <div className="sv2-cart-finder-mid">
                  <div className="sv2-cart-finder-name">{card.name}</div>
                  {card.meta ? <div className="sv2-cart-finder-meta">{card.meta}</div> : null}
                  <div className="sv2-cart-finder-row">
                    {card.priceUah != null ? (
                      <span className="sv2-cart-finder-price">{formatUah(card.priceUah)}</span>
                    ) : (
                      <span className="sv2-muted">{t(lang, "price_tbd")}</span>
                    )}
                        {justAdded ? (
                      <span className="sv2-cart-finder-incart" data-testid={`sv2-cart-inline-added-${card.id}`}>
                        {t(lang, "cart_added")}
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="sv2-cta sv2-secondary sv2-cart-finder-add"
                        data-testid={`sv2-cart-inline-add-btn-${card.id}`}
                        disabled={!canAdd}
                        onClick={() => addHit(card)}
                      >
                        {inCart ? `${t(lang, "cart_add_hit")} · ${qty}` : t(lang, "cart_add_hit")}
                      </button>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}
